const STORAGE_KEY = 'minibook_pwa_install_v1';

const SNOOZE_MS = [
  7 * 24 * 60 * 60 * 1000,
  30 * 24 * 60 * 60 * 1000,
  90 * 24 * 60 * 60 * 1000,
];

export const PWA_AUTO_MIN_SESSIONS = 2;
export const PWA_AUTO_SHOW_DELAY_MS = 18_000;
export const PWA_MAX_AUTO_IMPRESSIONS = 5;

export type PwaInstallStored = {
  sessionVisits: number;
  impressions: number;
  snoozeLevel: number;
  nextEligibleAt: number;
  installedMarked: boolean;
};

function defaultState(): PwaInstallStored {
  return {
    sessionVisits: 0,
    impressions: 0,
    snoozeLevel: 0,
    nextEligibleAt: 0,
    installedMarked: false,
  };
}

export function readPwaInstallState(): PwaInstallStored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw) as Partial<PwaInstallStored>;
    return {
      ...defaultState(),
      ...parsed,
      sessionVisits: Math.max(0, Number(parsed.sessionVisits) || 0),
      impressions: Math.max(0, Number(parsed.impressions) || 0),
      snoozeLevel: Math.max(0, Number(parsed.snoozeLevel) || 0),
      nextEligibleAt: Math.max(0, Number(parsed.nextEligibleAt) || 0),
      installedMarked: Boolean(parsed.installedMarked),
    };
  } catch {
    return defaultState();
  }
}

function writePwaInstallState(next: PwaInstallStored) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/** Один раз за сессию браузера увеличивает счётчик «визитов» для авто-показа. */
export function recordPwaInstallSessionVisit(): number {
  if (typeof sessionStorage === 'undefined') return readPwaInstallState().sessionVisits;
  const flag = 'pwa_install_visit_recorded';
  if (sessionStorage.getItem(flag)) {
    return readPwaInstallState().sessionVisits;
  }
  sessionStorage.setItem(flag, '1');
  const cur = readPwaInstallState();
  const sessionVisits = cur.sessionVisits + 1;
  writePwaInstallState({ ...cur, sessionVisits });
  return sessionVisits;
}

const SESSION_AUTO_KEY = 'pwa_install_auto_modal_shown';

export function wasPwaAutoModalShownThisSession(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(SESSION_AUTO_KEY) === '1';
}

export function markPwaAutoModalShownThisSession() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(SESSION_AUTO_KEY, '1');
}

export function shouldOfferPwaAutoModal(now: number, state: PwaInstallStored): boolean {
  if (state.installedMarked) return false;
  if (state.sessionVisits < PWA_AUTO_MIN_SESSIONS) return false;
  if (state.impressions >= PWA_MAX_AUTO_IMPRESSIONS) return false;
  if (state.snoozeLevel >= 4) return false;
  if (now < state.nextEligibleAt) return false;
  if (wasPwaAutoModalShownThisSession()) return false;
  return true;
}

export function bumpPwaImpression(): PwaInstallStored {
  const cur = readPwaInstallState();
  const next = { ...cur, impressions: cur.impressions + 1 };
  writePwaInstallState(next);
  return next;
}

export function onPwaInstallLater(): PwaInstallStored {
  const cur = readPwaInstallState();
  const snoozeLevel = cur.snoozeLevel + 1;
  let nextEligibleAt = Date.now();
  if (snoozeLevel <= SNOOZE_MS.length) {
    nextEligibleAt = Date.now() + SNOOZE_MS[snoozeLevel - 1];
  } else {
    nextEligibleAt = 4102444800000; // ~2100
  }
  const next = { ...cur, snoozeLevel, nextEligibleAt };
  writePwaInstallState(next);
  return next;
}

export function markPwaInstalledInStorage(): PwaInstallStored {
  const cur = readPwaInstallState();
  const next = { ...cur, installedMarked: true, snoozeLevel: 4, nextEligibleAt: 4102444800000 };
  writePwaInstallState(next);
  return next;
}
